import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TextField, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Typography, Box, Input, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { DebounceInput } from 'react-debounce-input';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '16px',
    padding: theme.spacing(2),
    backgroundColor: 'rgba(229, 248, 191, 0.83)',
    borderRadius: '8px',
    width: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#00913A',
    textAlign: 'center',
  },
  inputBox: {
    marginBottom: theme.spacing(2),
  },
  listItem: {
    paddingRight: theme.spacing(6),
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '8px',
    marginTop: '8px',
    maxHeight: '50px',
    width: '100%',
  },
}));

const DietaryNoteDialog = ({ notes, setNotes, viewOnly = false, handleSubmitBtnClicked, onDirtyChange }) => {
  const classes = useStyles();
  const [currentNote, setCurrentNote] = useState('');
  const [internalNotes, setInternalNotes] = useState(notes);
  const [isLoading, setIsLoading] = useState(false);
  const isDirty = useMemo(() => JSON.stringify(notes) !== JSON.stringify(internalNotes), [notes, internalNotes]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    setInternalNotes(notes);
  }, [notes]);

  const handleAddNote = () => {
    if (currentNote.trim() !== '') {
      setInternalNotes([...internalNotes, currentNote.trim()]);
      setCurrentNote('');
    }
  };

  const handleRemoveNote = (index) => {
    const newNotes = internalNotes.filter((_, i) => i !== index);
    setInternalNotes(newNotes);
  };

  const handleUpdateNote = (event, index) => {
    const newNotes = [...internalNotes];
    newNotes[index] = event.target.value;
    setInternalNotes(newNotes);
  };

  const handleNoteChange = (event) => {
    setCurrentNote(event.target.value);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleAddNote();
      event.preventDefault(); // Prevent form submission if it's part of a form
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    await handleSubmitBtnClicked(internalNotes);
    setIsLoading(false);
  };

  return (
    <Box className={classes.root}>
      <Typography variant="subtitle1" gutterBottom className={classes.title}>
        飲食備註
      </Typography>
      {!viewOnly && <Box display="flex" alignItems="center" className={classes.inputBox}>
        <TextField
          label="新增飲食備註"
          variant="outlined"
          value={currentNote}
          onChange={handleNoteChange}
          onKeyPress={handleKeyPress}
          fullWidth
          size="small"
        />
        <IconButton onClick={handleAddNote} color="primary" sx={{ ml: 1 }} disabled={!currentNote.trim()}>
          <AddCircleOutlineIcon />
        </IconButton>
      </Box>}
      {internalNotes.length > 0 && (
        <List dense>
          {internalNotes.map((note, index) => (
            <ListItem key={index} divider className={classes.listItem}>
              {viewOnly ? (
                <ListItemText primary={note} />
              ) : (
                <DebounceInput
                  element={Input}
                  fullWidth
                  value={note}
                  onChange={(event) => handleUpdateNote(event, index)}
                  debounceTimeout={300}
                  style={{ marginLeft: 8 }}
                  id={`dietary-note-${index}`}
                  disableUnderline
                />
              )}
              <ListItemSecondaryAction>
                {!viewOnly && <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveNote(index)} color="secondary">
                  <RemoveCircleOutlineIcon />
                </IconButton>}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
      {!viewOnly && (
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!isDirty || isLoading}>
            送出
          </Button>
        </Box>
      )}
    </Box>
  );
};

DietaryNoteDialog.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.string).isRequired,
  setNotes: PropTypes.func.isRequired,
  viewOnly: PropTypes.bool,
  handleSubmitBtnClicked: PropTypes.func,
  onDirtyChange: PropTypes.func,
};


export default DietaryNoteDialog;
